import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import StepByStep from '../../../StepByStep';
import { fetchAccountAddresses } from '../../../../store/actions/account/accountActions';
import { selectBalancesAvailable } from '../../../../store/selectors/portfolioBalancesSelectors';
import WireInformationReview from './Wire/WireInformationReview';
import { iraFeeWithdrawalSubmit } from '../../../../store/actions/orders/orderActions';
import {
  IRA_FEE_WITHDRAWAL,
  TYPE_IRA_FEE_WIRE_WITHDRAWAL,
} from '../../../../util/constants';
import Status from '../../Status';
import {
  closeWizardContent,
  setWizardContent,
} from '../../../../util/helpers/wizardHelpers';
import WireInformationForm from './Wire/WireInformationForm';
import {
  PREFUNDED_WIRE_WITHDRAWAL_WIZARD,
  STATUS_MESSAGE,
  STATUS_MODAL,
  WITHDRAWAL_VALUE,
} from '../../../../constants/sessionStorage';
import {
  WITHDRAWAL_PREFUNDED_WIZARD_TYPES,
  WITHDRAWAL_WIZARD_TYPES,
} from '../../../../constants/standardWithdrawalWizardConstants';
import { useSessionStorageState } from '../../../../util/hooks/useSessionStorageState';
import { FUND_WITHDRAW_COMPLETE } from '../../../../constants/gtmEvents';
import useGtmHook from '../../../../util/hooks/useGtmHook';

const PrefundedWireWithdrawalWizard = ({
  accountUid,
  closeModal,
  handleRefreshTransactions,
  goBack,
  type,
  setWithdrawalType,
  setTitleType,
}) => {
  const dispatch = useDispatch();
  const balances = useSelector(selectBalancesAvailable);
  const { fundGtmEvent } = useGtmHook();

  const [availableMoney, setAvailableMoney] = useState(0);
  const [value, setValue] = useSessionStorageState(WITHDRAWAL_VALUE, {
    amount: 0,
  });
  const [status, setStatus] = useSessionStorageState(STATUS_MODAL, null);
  const [statusMessage, setStatusMessage] = useSessionStorageState(
    STATUS_MESSAGE,
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const setWithdrawValue = (value) => {
    setWizardContent(WITHDRAWAL_VALUE, value);
    setValue(value);
  };

  const setWithdrawStatus = (value) => {
    setWizardContent(STATUS_MODAL, value);
    setStatus(value);
  };

  const setWithdrawStatusMessage = (value) => {
    setWizardContent(STATUS_MESSAGE, value);
    setStatusMessage(value);
  };

  const closeStatusModal = () => {
    closeModal();
    closeWizardContent();
  };

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [accountUid, dispatch]);

  useEffect(() => {
    const availableMoney = balances?.AvailablePrefundedStorageFees;
    setAvailableMoney(availableMoney);
  }, [balances]);

  useEffect(() => {
    setTitleType(WITHDRAWAL_PREFUNDED_WIZARD_TYPES.WIRE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmWithdraw = (goStepForward) => {
    setIsLoading(true);

    const onRequestSuccess = () => {
      handleRefreshTransactions();
      fundGtmEvent(FUND_WITHDRAW_COMPLETE, {
        fund: {
          method: WITHDRAWAL_WIZARD_TYPES.WIRE,
          amount: value.amount,
        },
      });
    };

    const handleResponse = (hasError, message) => {
      setWithdrawStatus(hasError);
      setWithdrawStatusMessage(message);
      goStepForward();
      setIsLoading(false);
    };

    if (!isEmpty(value)) {
      const data = {
        Amount: value.amount,
        IRAFeeWithdrawalType: TYPE_IRA_FEE_WIRE_WITHDRAWAL,
        AccountUid: accountUid,
        Type: IRA_FEE_WITHDRAWAL,
      };
      dispatch(
        iraFeeWithdrawalSubmit({
          accountUid,
          data,
          handleResponse,
          onRequestSuccess,
        }),
      );
    }
  };

  const steps = [WireInformationForm, WireInformationReview, Status];

  return (
    <StepByStep sessionScopeStep={PREFUNDED_WIRE_WITHDRAWAL_WIZARD}>
      {({ goStepForward, goStepBack }) =>
        steps.map((buyStorageStep) =>
          React.createElement(buyStorageStep, {
            handleBack: goStepBack,
            handleNext: goStepForward,
            handleClose: closeModal,
            goBack,
            availableMoney,
            setValue: setWithdrawValue,
            value,
            confirmWithdraw,
            type,
            setWithdrawalType,
            // These ones are for the Status Modal
            hasError: status,
            onButtonClick: closeStatusModal,
            text: statusMessage,
            backButtonText: 'Close',
            isLoading,
          }),
        )
      }
    </StepByStep>
  );
};

PrefundedWireWithdrawalWizard.propTypes = {
  closeModal: PropTypes.func,
  accountUid: PropTypes.string,
  goBack: PropTypes.func,
  handleRefreshTransactions: PropTypes.func,
  type: PropTypes.string,
  setWithdrawalType: PropTypes.func,
  setTitleType: PropTypes.func,
};

export default PrefundedWireWithdrawalWizard;
